import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { PriorityBadge } from "../components/Badges";
import DevTable from "../components/DevTable";
import GroupedDevTable from "../components/GroupedDevTable";
import KanbanBoard from "../components/KanbanBoard";
import DevDialog from "../components/DevDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TASK_ESTADOS, PRIORITIES, STATUSES, fmtDate } from "../lib/constants";
import { Briefcase, ListChecks, KanbanSquare, Table2, Layers, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function StatChip({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className="border border-border bg-white rounded-sm px-4 py-3">
      <div className={`font-heading text-3xl font-extrabold tracking-tight ${accent || "text-slate-900"}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

export default function MyWork() {
  const navigate = useNavigate();
  const { canWrite, isPM } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState({ sistemas: [] });
  const [allDevs, setAllDevs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [groupBy, setGroupBy] = useState("none");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/my-work").then((r) => setData(r.data)).catch(() => setData({ developments: [], tasks: [], counts: {} }));
  }, []);

  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/catalogs").then((r) => setCatalog(r.data));
    api.get("/developments").then((r) => setAllDevs(r.data));
  }, [load]);

  const departamentoOptions = Array.from(new Set(allDevs.map((d) => d.departamento_solicitante).filter(Boolean))).sort();
  const solicitanteOptions = Array.from(new Set(allDevs.map((d) => d.solicitante).filter(Boolean))).sort();

  const openEdit = (d) => { setEditing(d); setDialogOpen(true); };

  const handleMove = async (id, newStatus) => {
    const dev = data.developments.find((d) => d.id === id);
    setData((prev) => ({ ...prev, developments: prev.developments.map((d) => (d.id === id ? { ...d, status: newStatus } : d)) }));
    try {
      await api.put(`/developments/${id}`, { status: newStatus });
      toast.success(`${dev.dev_id} → ${STATUSES[newStatus].label}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/developments/${toDelete.id}`);
      toast.success("Desarrollo eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleNotify = async (dev) => {
    toast.loading("Enviando correo...", { id: "notify" });
    try {
      const { data: res } = await api.post(`/developments/${dev.id}/notify`);
      toast.success(`Correo enviado a ${res.recipients.length} destinatario(s)`, { id: "notify" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "notify" });
    }
  };

  if (!data) return <div className="p-8 text-slate-400">Cargando...</div>;

  const c = data.counts || {};

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6">
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Personal</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2">
          <Briefcase className="h-7 w-7" /> Mi trabajo
        </h1>
      </div>

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatChip testid="stat-devs-active" label="Desarrollos activos" value={c.developments_active ?? 0} />
          <StatChip testid="stat-tasks-active" label="Tareas activas" value={c.tasks_active ?? 0} />
          <StatChip testid="stat-overdue" label="Vencidos" value={c.overdue ?? 0} accent={(c.overdue ?? 0) > 0 ? "text-red-600" : "text-slate-900"} />
          <StatChip testid="stat-total" label="Total asignado" value={(c.developments_total ?? 0) + (c.tasks_total ?? 0)} />
        </div>

        {/* Mis desarrollos: tabla + kanban con edición */}
        <div>
          <h2 className="text-lg font-bold tracking-tight flex items-center gap-2 mb-3">
            <KanbanSquare className="h-5 w-5 text-slate-500" /> Mis desarrollos
            <span className="font-mono text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">{data.developments.length}</span>
          </h2>

          <Tabs defaultValue="table">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <TabsList className="rounded-sm bg-slate-100">
                <TabsTrigger value="table" data-testid="mywork-tab-table" className="rounded-sm data-[state=active]:bg-white"><Table2 className="h-4 w-4 mr-1.5" />Tabla</TabsTrigger>
                <TabsTrigger value="kanban" data-testid="mywork-tab-kanban" className="rounded-sm data-[state=active]:bg-white"><KanbanSquare className="h-4 w-4 mr-1.5" />Kanban</TabsTrigger>
              </TabsList>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger data-testid="mywork-group-by" className="rounded-sm w-44 h-9">
                  <Layers className="h-4 w-4 mr-1.5 text-slate-400" /><SelectValue placeholder="Agrupar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin agrupar</SelectItem>
                  <SelectItem value="status">Agrupar por estado</SelectItem>
                  <SelectItem value="responsable">Agrupar por responsable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <TabsContent value="table">
              {data.developments.length === 0
                ? <div className="border border-border bg-white rounded-sm px-4 py-10 text-center text-slate-400 text-sm">No tienes desarrollos asignados.</div>
                : <GroupedDevTable devs={data.developments} groupBy={groupBy} onRowClick={openEdit} onDelete={setToDelete} onNotify={handleNotify} isPM={isPM} canWrite={canWrite} />}
            </TabsContent>
            <TabsContent value="kanban">
              <KanbanBoard devs={data.developments} onMove={handleMove} onCardClick={openEdit} canWrite={canWrite} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Mis tareas */}
        <div>
          <h2 className="text-lg font-bold tracking-tight flex items-center gap-2 mb-3">
            <ListChecks className="h-5 w-5 text-slate-500" /> Mis tareas
            <span className="font-mono text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-sm">{data.tasks.length}</span>
          </h2>
          <div className="border border-border bg-white rounded-sm divide-y divide-border" data-testid="my-tasks">
            {data.tasks.length === 0 && (
              <div className="px-4 py-10 text-center text-slate-400 text-sm">No tienes tareas asignadas.</div>
            )}
            {data.tasks.map((t) => (
              <div key={t.id} data-testid={`mywork-task-${t.id}`} onClick={() => navigate("/tareas")}
                className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors duration-150">
                {t.estado === "completada"
                  ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  : <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PRIORITIES[t.priority]?.dot }} />}
                <div className="flex-1 min-w-0">
                  <span className={`font-semibold ${t.estado === "completada" ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</span>
                  {t.description && <div className="text-[11px] text-slate-500 truncate mt-0.5">{t.description}</div>}
                </div>
                <PriorityBadge priority={t.priority} />
                <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${TASK_ESTADOS[t.estado]?.cls}`}>
                  {TASK_ESTADOS[t.estado]?.label}
                </span>
                <span className="font-mono text-xs w-20 text-right text-slate-500">{fmtDate(t.fecha_limite)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DevDialog open={dialogOpen} onOpenChange={setDialogOpen} dev={editing} users={users} allDevs={data.developments} onSaved={load}
        catalog={catalog} onCatalogChange={setCatalog} departamentoOptions={departamentoOptions} solicitanteOptions={solicitanteOptions} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar desarrollo?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{toDelete?.title}</strong>. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="mywork-confirm-delete" onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
